/**
 * Font Loader Utility
 *
 * Handles dynamic loading of Google Fonts for brand packages and themes.
 */

const GOOGLE_FONTS_API = 'https://fonts.googleapis.com/css2';
const loadedFonts = new Set<string>();

/**
 * Available fonts for brand packages
 * Curated list of popular Google Fonts that work well for web design
 */
export const AVAILABLE_FONTS = [
  // Sans-serif - Good for headings and body
  { name: 'Inter', category: 'sans-serif', weights: '300;400;500;600;700' },
  { name: 'Montserrat', category: 'sans-serif', weights: '300;400;500;600;700' },
  { name: 'Poppins', category: 'sans-serif', weights: '300;400;500;600;700' },
  { name: 'Raleway', category: 'sans-serif', weights: '300;400;500;600;700' },
  { name: 'Open Sans', category: 'sans-serif', weights: '300;400;500;600;700' },
  { name: 'Lato', category: 'sans-serif', weights: '300;400;700' },
  { name: 'Roboto', category: 'sans-serif', weights: '300;400;500;700' },
  { name: 'Source Sans Pro', category: 'sans-serif', weights: '300;400;600;700' },
  { name: 'Nunito', category: 'sans-serif', weights: '300;400;600;700' },
  { name: 'DM Sans', category: 'sans-serif', weights: '300;400;500;700' },
  { name: 'Work Sans', category: 'sans-serif', weights: '300;400;500;600;700' },
  { name: 'Outfit', category: 'sans-serif', weights: '300;400;500;600;700' },

  // Serif - Good for elegant headings
  { name: 'Playfair Display', category: 'serif', weights: '400;500;600;700' },
  { name: 'Merriweather', category: 'serif', weights: '300;400;700' },
  { name: 'Lora', category: 'serif', weights: '400;500;600;700' },
  { name: 'Libre Baskerville', category: 'serif', weights: '400;700' },

  // Display - For bold headlines
  { name: 'Oswald', category: 'sans-serif', weights: '300;400;500;600;700' },
  { name: 'Bebas Neue', category: 'display', weights: '400' },
] as const;

export type AvailableFont = (typeof AVAILABLE_FONTS)[number]['name'];

/**
 * Get font info by name
 */
export function getFontInfo(fontName: string) {
  return AVAILABLE_FONTS.find(f => f.name === fontName);
}

/**
 * Load a Google Font dynamically
 * Only loads if not already loaded
 */
export function loadGoogleFont(fontName: string): void {
  if (!fontName || loadedFonts.has(fontName)) {
    return;
  }

  const fontInfo = getFontInfo(fontName);
  const weights = fontInfo?.weights || '300;400;500;600;700';

  // Create link element for Google Fonts
  const link = document.createElement('link');
  link.href = `${GOOGLE_FONTS_API}?family=${encodeURIComponent(fontName)}:wght@${weights}&display=swap`;
  link.rel = 'stylesheet';
  link.setAttribute('data-font', fontName);

  document.head.appendChild(link);
  loadedFonts.add(fontName);

  console.log(`🔤 FontLoader: Loaded font "${fontName}"`);
}

/**
 * Load multiple fonts at once
 */
export function loadGoogleFonts(fontNames: string[]): void {
  fontNames.forEach(fontName => {
    if (fontName) {
      loadGoogleFont(fontName);
    }
  });
}

/**
 * Check if a font is already loaded
 */
export function isFontLoaded(fontName: string): boolean {
  return loadedFonts.has(fontName);
}

/**
 * Get CSS font-family string with fallbacks
 */
export function getFontFamily(
  fontName: string | undefined,
  fallback = 'system-ui, sans-serif'
): string {
  if (!fontName) {
    return fallback;
  }
  return `"${fontName}", ${fallback}`;
}

/**
 * Preload all available fonts (useful for the font selector UI)
 * This loads fonts in the background so previews work
 */
export function preloadAllFonts(): void {
  // Load fonts in batches to avoid overwhelming the browser
  const batchSize = 4;
  let index = 0;

  const loadBatch = () => {
    const batch = AVAILABLE_FONTS.slice(index, index + batchSize);
    batch.forEach(font => loadGoogleFont(font.name));
    index += batchSize;

    if (index < AVAILABLE_FONTS.length) {
      // Load next batch after a short delay
      setTimeout(loadBatch, 100);
    }
  };

  loadBatch();
}
